// @greenlight-spec: 32-knowledge.md
// @greenlight-spec: 28-it-dashboard-ui.md
import type { KnowledgeEntrySummary, KnowledgeScope } from '@greenlight/contract';

import { canMutateScope, formatKnowledgeDate } from '@/lib/knowledgeUtils';

// canMutateScope and formatKnowledgeDate are re-exported from lib/ (the canonical
// location) so cross-feature callers can import from @/lib/knowledgeUtils without
// depending on this feature folder.
export { canMutateScope, formatKnowledgeDate };

export interface ScopeDisplay {
  scopeLabel: string;
  tile: {
    scope: KnowledgeScope;
    integrationSlug?: string;
    integrationName?: string;
    appName?: string;
    appSlug?: string;
  };
}

/** Resolve the human scope label + tile props for an entry/proposal, given the org's integration + app maps. */
export function resolveScopeDisplay(
  refs: { scope: KnowledgeScope; integrationId: string | null; appId: string | null },
  integrationsById: ReadonlyMap<string, { slug: string; name: string }>,
  appsById: ReadonlyMap<string, { slug: string; name: string }>,
): ScopeDisplay {
  if (refs.scope === 'integration') {
    const found = refs.integrationId ? integrationsById.get(refs.integrationId) : undefined;
    const name = found?.name ?? 'Integration';
    const slug = found?.slug ?? '';
    return {
      scopeLabel: `Integration · ${name}`,
      tile: { scope: 'integration', integrationSlug: slug, integrationName: name },
    };
  }
  if (refs.scope === 'app') {
    const found = refs.appId ? appsById.get(refs.appId) : undefined;
    return {
      scopeLabel: `App · ${found?.slug ?? refs.appId ?? ''}`,
      tile: { scope: 'app', appName: found?.name, appSlug: found?.slug },
    };
  }
  return { scopeLabel: 'Org', tile: { scope: 'org' } };
}

export function knowledgeEditorLabel(
  e: KnowledgeEntrySummary['lastEditor'],
  kind: 'human' | 'agent',
): string {
  if (kind === 'agent') return 'agent';
  return e.email ?? 'unknown';
}

/** Compact relative time like "now", "5h ago", "4d ago", "1mo ago". Falls back to formatKnowledgeDate for >12mo or invalid input. */
export function formatRelativeDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const deltaS = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (deltaS < 60) return 'now';
  const m = Math.floor(deltaS / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(deltaS / 3600);
  if (h < 24) return `${h}h ago`;
  const day = Math.floor(deltaS / 86400);
  if (day < 30) return `${day}d ago`;
  // Coarse label — months approximated as 30 days is fine for a relative "Nmo ago".
  const mo = Math.floor(deltaS / (30 * 86400));
  if (mo < 12) return `${mo}mo ago`;
  return formatKnowledgeDate(iso);
}
